/**
 * RoomMenuTrigger renders the button that opens the room menu.
 *
 * Why: The trigger button has hover effects and focus styling that are
 * specific to this component. Extracting it keeps RoomMenu focused on
 * dialog logic and content.
 *
 * When to use: Used internally by RoomMenu as the Dialog.Trigger.
 * Must use forwardRef and forward props to work with Radix Dialog.Trigger asChild.
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { MerelsMarkIcon } from '../../library/Icons/MerelsMarkIcon'
import { roomMenuTestIds } from './roomMenu.testIds'

export interface RoomMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Ref for focus management (from header navigation) */
  triggerRef?: React.Ref<HTMLButtonElement>
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

/** Merge multiple refs into one callback ref */
function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value
      }
    })
  }
}

export const RoomMenuTrigger = React.forwardRef<HTMLButtonElement, RoomMenuTriggerProps>(
  ({ triggerRef, className, tabIndex, isFocused, accentColor, accentInkColor = '#ffffff', backgroundColor, ...props }, ref) => {
    return (
      <IconButton
        {...props}
        ref={mergeRefs(ref, triggerRef)}
        data-testid={roomMenuTestIds.trigger}
        className={cn(
          "inline-flex h-[var(--menu-btn-size-merels,36px)] w-[var(--menu-btn-size-merels,36px)] items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus p-1",
          isFocused && "outline-none",
          className
        )}
        style={{
          ...(accentColor ? { color: accentColor } : {}),
          ...(isFocused && accentColor ? {
            boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
          } : {}),
        }}
        tabIndex={tabIndex}
        aria-label="Room menu"
        onMouseEnter={(e) => {
          if (accentColor) {
            e.currentTarget.style.backgroundColor = accentColor
            e.currentTarget.style.color = accentInkColor
          }
        }}
        onMouseLeave={(e) => {
          if (accentColor) {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = accentColor
          }
        }}
        icon={<MerelsMarkIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />}
      />
    )
  }
)
RoomMenuTrigger.displayName = 'RoomMenuTrigger'
